import { currencyLabel } from './money';
import { getCurrency } from './currency';

/**
 * What a collection's Currency setting shows (docs/plans/
 * localization-preferences.md §2, the owner's ruling L3), from the collection
 * as the settings form holds it — the time zone field's rules
 * (utils/collectionTimezone.js), with one difference: the cascade has no
 * platform tier. A collection with no currency of its own and none above it
 * is NOT SET, and new money there starts in its author's own currency.
 *
 *   collection.currency          the form's value — a code set here, or null
 *                                / absent to inherit
 *   collection.currencyResolved  keepr-api's answer at load:
 *                                { code, source: 'collection' | 'parent' |
 *                                'none', from?, fallback?, ignored? }
 *
 * Returns null when the response carries no `currencyResolved` (an API that
 * predates the setting, or a collection not saved yet).
 *
 *   own            true when the collection sets its own currency
 *   ownCode        that code (the picker's value), else null
 *   inheritedCode  what it uses without one, else null (nothing above)
 *   placeholder    the picker's words while inheriting
 *   mark           the SourceMark's words
 *   fallbackText   where "Reset to inherited" lands
 *   ignored        a stored value the server skipped, while the form holds it
 */
export function describeCollectionCurrency( collection ) {
	const resolved = collection?.currencyResolved;
	if ( ! resolved || typeof resolved.source !== 'string' ) return null;

	const inherited = resolved.source === 'collection'
		? ( resolved.fallback && typeof resolved.fallback.source === 'string' ? resolved.fallback : { code: null, source: 'none' } )
		: resolved;
	const fromParent = inherited.source === 'parent' && typeof inherited.code === 'string' && inherited.code;
	const parentName = typeof inherited.from?.name === 'string' && inherited.from.name.trim() ? inherited.from.name.trim() : '';

	const formValue = typeof collection.currency === 'string' && collection.currency ? collection.currency : null;
	const skipped = resolved.source !== 'collection' && typeof resolved.ignored === 'string' && formValue === resolved.ignored;
	const ownCode = formValue && ! skipped && getCurrency( formValue ) ? formValue : null;

	const inheritedWords = fromParent ? `Inherited from ${ parentName || 'the parent collection' }` : 'Not set';

	return {
		own: Boolean( ownCode ),
		ownCode,
		inheritedCode: fromParent ? inherited.code : null,
		placeholder: fromParent ? currencyLabel( inherited.code ) : 'Not set',
		mark: ownCode
			? { word: 'This collection', phrase: 'Set on this collection', own: true }
			: { word: inheritedWords, phrase: inheritedWords, own: false },
		fallbackText: fromParent
			? `Falls back to ${ parentName ? `${ parentName }'s` : 'the parent collection\'s' } currency, ${ inherited.code }.`
			: 'Falls back to each author\'s own currency.',
		ignored: skipped ? formValue : null,
	};
}

/**
 * The code a collection's new money starts in, from keepr-api's resolved
 * answer — its own, else the nearest collection above that sets one — or
 * null when none is set (or the collection is not loaded).
 */
export function collectionCurrencyCode( collection ) {
	const code = collection?.currencyResolved?.code;
	return typeof code === 'string' && getCurrency( code ) ? code : null;
}
